import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  Loader2,
  Vault,
  X,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Marketplace — convert Free Play (FP) credits into main wallet balance.
 * Items + redemption stay wired to /api/marketplace/* (admin-configurable).
 */
const FALLBACK_ITEMS = [
  { id: 'starter-1', name: 'Quick Cash', fp: 100, cash: 20, cat: 'starter', sort_order: 1 },
  { id: 'starter-2', name: 'Mini Vault', fp: 250, cash: 50, cat: 'starter', sort_order: 2 },
  { id: 'mid-1', name: 'Lucky Stack', fp: 500, cash: 100, cat: 'mid', sort_order: 3 },
];

const MARKETPLACE_ASSET_BASE = '/assets/marketplace';
const MARKETPLACE_CUT_BASE = `${MARKETPLACE_ASSET_BASE}/png-cuts`;
const MARKETPLACE_HERO_SRC = `${MARKETPLACE_CUT_BASE}/marketplace-hero.png`;
const DEMO_MARKETPLACE_FP_KEY = 'slotsoceans_demo_marketplace_fp_balance';
const DEMO_MARKETPLACE_STARTING_FP = 1000;

const FIXED_MARKETPLACE_OFFERS = [
  { ...FALLBACK_ITEMS[0], imageSrc: `${MARKETPLACE_CUT_BASE}/marketplace-quick-cash.png` },
  { ...FALLBACK_ITEMS[1], imageSrc: `${MARKETPLACE_CUT_BASE}/marketplace-mini-vault.png` },
  { ...FALLBACK_ITEMS[2], imageSrc: `${MARKETPLACE_CUT_BASE}/marketplace-lucky-stack.png` },
];

const CATEGORY_STYLE = {
  starter: {
    label: 'Starter',
    badge: 'linear-gradient(180deg, #6D8CFF, #294BCE)',
    panel: 'linear-gradient(145deg, rgba(13,190,255,0.96), rgba(0,95,214,0.95) 54%, rgba(0,38,121,0.96))',
    border: '#4BE5FF',
    glow: 'rgba(55,211,255,0.45)',
  },
  mid: {
    label: 'Mid',
    badge: 'linear-gradient(180deg, #3DE2D8, #096B9C)',
    panel: 'linear-gradient(145deg, rgba(12,164,235,0.96), rgba(0,75,178,0.95) 52%, rgba(19,44,126,0.97))',
    border: '#49F4D6',
    glow: 'rgba(73,244,214,0.36)',
  },
  high: {
    label: 'High',
    badge: 'linear-gradient(180deg, #9BD5FF, #564DFF)',
    panel: 'linear-gradient(145deg, rgba(40,125,255,0.96), rgba(36,67,196,0.96) 52%, rgba(27,35,115,0.98))',
    border: '#8EC7FF',
    glow: 'rgba(142,199,255,0.34)',
  },
  whale: {
    label: 'Whale',
    badge: 'linear-gradient(180deg, #FFE66D, #FF9F1C)',
    panel: 'linear-gradient(145deg, rgba(10,143,178,0.96), rgba(22,75,160,0.96) 48%, rgba(87,31,116,0.97))',
    border: '#FFE66D',
    glow: 'rgba(255,214,70,0.38)',
  },
};

const formatNumber = (value, decimals = 0) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? Math.max(0, n) : 0;
  return safe.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatFpBalance = (value) => formatNumber(Math.floor(Number(value) || 0), 0);

const readDemoFpBalance = () => {
  if (typeof window === 'undefined') return DEMO_MARKETPLACE_STARTING_FP;
  const stored = Number(window.localStorage.getItem(DEMO_MARKETPLACE_FP_KEY));
  return Number.isFinite(stored) && stored >= 0 ? stored : DEMO_MARKETPLACE_STARTING_FP;
};

const writeDemoFpBalance = (value) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(DEMO_MARKETPLACE_FP_KEY, String(Math.max(0, Number(value) || 0)));
};

function normalizeItem(item, index) {
  return {
    id: item?.id || `marketplace-${index}`,
    name: item?.name || `Reward ${index + 1}`,
    fp: Number(item?.fp || 0),
    cash: Number(item?.cash || 0),
    cat: CATEGORY_STYLE[item?.cat] ? item.cat : 'starter',
    sort_order: Number(item?.sort_order ?? index),
    active: item?.active !== false,
  };
}

function fixedMarketplaceItems(items) {
  const normalized = items
    .map(normalizeItem)
    .filter((item) => item.active && item.fp > 0 && item.cash > 0)
    .sort((a, b) => a.sort_order - b.sort_order);

  return FIXED_MARKETPLACE_OFFERS.map((offer, index) => {
    const match = normalized.find((item) => (
      item.fp === offer.fp && item.cash === offer.cash
    )) || normalized.find((item) => (
      String(item.name || '').trim().toLowerCase() === offer.name.toLowerCase()
    ));

    return {
      ...(match || {}),
      ...offer,
      id: match?.id || offer.id,
      sort_order: index + 1,
      active: true,
    };
  });
}

export default function MarketplacePage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';
  const isLocalDemoPlayer = user?.id === 'demo-player-1';

  const [items, setItems] = useState(FALLBACK_ITEMS);
  const [fpBalance, setFpBalance] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [redeemed, setRedeemed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [marketplaceEnabled, setMarketplaceEnabled] = useState(false);

  const displayItems = useMemo(
    () => (marketplaceEnabled ? fixedMarketplaceItems(items) : []),
    [items, marketplaceEnabled]
  );

  const refreshBalance = async () => {
    if (isLocalDemoPlayer) {
      setFpBalance(readDemoFpBalance());
      return;
    }
    try {
      const w = await api.get('/api/wallet/balance');
      const nextFp = Number(w.data?.play_credits ?? w.data?.fp_credits ?? w.data?.play_credits_balance ?? 0);
      setFpBalance(Number.isFinite(nextFp) ? nextFp : 0);
    } catch (_) {
      setFpBalance(0);
    }
  };

  useEffect(() => {
    let mounted = true;
    api.get('/api/marketplace/items')
      .then((r) => {
        if (mounted) {
          const enabled = r.data?.enabled !== false;
          setMarketplaceEnabled(enabled);
          if (enabled && Array.isArray(r.data?.items) && r.data.items.length) {
            setItems(r.data.items);
          }
        }
      })
      .catch(() => {
        if (mounted) setMarketplaceEnabled(false);
      });
    refreshBalance();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalDemoPlayer]);

  const onRedeem = (item) => setConfirm(item);

  const onConfirm = async () => {
    if (!confirm || submitting) return;
    if (fpBalance < confirm.fp) {
      toast.error(`Need ${formatNumber(confirm.fp)} FP — you have ${formatFpBalance(fpBalance)} FP`);
      return;
    }
    setSubmitting(true);
    try {
      if (isLocalDemoPlayer) {
        const nextFpBalance = Math.max(0, fpBalance - confirm.fp);
        writeDemoFpBalance(nextFpBalance);
        setFpBalance(nextFpBalance);
      } else {
        await api.post('/api/marketplace/redeem', { item_id: confirm.id });
      }
      setConfirm(null);
      setRedeemed(confirm);
      if (!isLocalDemoPlayer) await refreshBalance();
      toast.success(`$${formatNumber(confirm.cash)} added to main balance`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message || 'Redemption failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="marketplace-png-page player-app min-h-screen pb-10"
      style={{ '--marketplace-bg': "url('/assets/slots-oceans-auth-bg.png')" }}
      data-testid="explore-sub-marketplace"
    >
      <section className="marketplace-png-hero">
        <img
          className="marketplace-png-hero-img"
          src={MARKETPLACE_HERO_SRC}
          alt="Marketplace. Exchange your FP credits to main balance."
          draggable="false"
        />
        <button
          type="button"
          onClick={() => navigate(explorePath)}
          className="marketplace-png-back"
          data-testid="marketplace-back"
          aria-label="Back to rewards"
        />
      </section>

      <main className="marketplace-png-main" aria-label="Marketplace rewards">
        {displayItems.length === 0 ? (
          <div className="marketplace-ocean-empty">
            <Vault className="h-12 w-12" strokeWidth={2.5} />
            <h2>{marketplaceEnabled ? 'No marketplace offers yet' : 'Marketplace is unavailable'}</h2>
            <p>{marketplaceEnabled ? 'Check back later for FP exchange rewards.' : 'FP exchange rewards are currently disabled.'}</p>
          </div>
        ) : (
          <div className="marketplace-png-list">
            {displayItems.map((item, index) => {
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onRedeem(item)}
                  className="marketplace-png-card"
                  data-testid={`mp-item-${index}`}
                  aria-label={`${item.name}: spend ${formatNumber(item.fp)} FP and get $${formatNumber(item.cash)}. Redeem.`}
                >
                  <img
                    src={item.imageSrc}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                  />
                </button>
              );
            })}
          </div>
        )}
      </main>

      {confirm && (
        <div
          className="marketplace-modal-shell"
          role="dialog"
          aria-modal="true"
          aria-label={`Confirm ${confirm.name} redemption`}
          onClick={() => setConfirm(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="marketplace-modal-card marketplace-modal-card--confirm"
            data-testid="mp-confirm-modal"
          >
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className="marketplace-modal-close"
              aria-label="Close"
              data-testid="mp-confirm-close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="marketplace-modal-selected" data-testid="mp-selected-card-preview">
              <img
                src={confirm.imageSrc}
                alt={`${confirm.name} reward card`}
                draggable="false"
              />
            </div>

            <div className="marketplace-modal-decision">
              <p className="marketplace-modal-balance" data-testid="mp-fp-balance">
                Your FP balance: <b>{formatFpBalance(fpBalance)} FP</b>
              </p>

              <div className="marketplace-modal-actions">
                <button
                  type="button"
                  onClick={() => setConfirm(null)}
                  disabled={submitting}
                  className="marketplace-modal-cancel"
                  data-testid="mp-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={submitting || fpBalance < confirm.fp}
                  className="marketplace-modal-confirm"
                  data-testid="mp-confirm-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Redeeming
                    </>
                  ) : fpBalance < confirm.fp ? (
                    'Insufficient FP'
                  ) : (
                    'Confirm'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {redeemed && (
        <div
          className="marketplace-modal-shell"
          role="dialog"
          aria-modal="true"
          aria-labelledby="marketplace-success-title"
          onClick={() => setRedeemed(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="marketplace-modal-card marketplace-modal-card--success"
            data-testid="mp-success-modal"
          >
            <div className="marketplace-success-icon">
              <CheckCircle2 className="h-11 w-11" strokeWidth={2.6} />
            </div>
            <h2 id="marketplace-success-title" className="marketplace-modal-title">Redeemed!</h2>
            <p className="marketplace-modal-copy">
              {redeemed.name} converted to ${formatNumber(redeemed.cash)} main balance.
            </p>
            <button
              type="button"
              onClick={() => setRedeemed(null)}
              className="marketplace-modal-confirm marketplace-modal-confirm--full"
              data-testid="mp-success-ok"
            >
              Awesome
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
